// Godmode — Freiflug nur für Entwicklung (7,5 km Strecke zu Fuß = unbrauchbar).
//
// Eigene Datei, damit player.js klein bleibt und parallele Kollisions-Arbeit
// dort nicht mit Fluglogik verheddert. Kollision/Gravitation fragen
// `state.mode === 'fly'` ab — das bleibt stabil, wenn Boden-Kollision wächst.
//
// Netz: bewusst kein sendInput im Flug (siehe player._sendNet). Ein Avatar
// mit 300 m/s durch die Luft widerspricht Konzept §8/§10 und spoilt den Hof.

import { terrainHeight, isVoidAt } from '../../shared/terrain.js';
import { projectToTrack, trackFrame } from '../../shared/track.js';

// Dutzende m/s normal, Hunderte mit Shift — 7,5 km in Sekunden erreichbar.
export const FLY_SPEED = 52;        // m/s ≈ 187 km/h
export const FLY_SPEED_FAST = 340;  // m/s ≈ 1224 km/h

// Wiederverwendete Structs — nichts pro Frame allokieren.
const _wish = { x: 0, y: 0, z: 0 };
const _frame = {
  pos: { x: 0, y: 0, z: 0 },
  tangent: { x: 0, y: 0, z: 0 },
  right: { x: 0, y: 0, z: 0 },
  heading: 0,
};

/**
 * In den Freiflug. Löst Zugbindung, nullt Geschwindigkeit.
 * @param {object} player  PlayerSystem-Instanz (nutzt .state, .hangGrab, …)
 */
export function enterGodMode(player) {
  const st = player.state;
  st.mode = 'fly';
  st.onCarId = null;
  st.grounded = false;
  st.crouching = false;
  st.velocity.set(0, 0, 0);
  st.canGrab = false;
  st.grabCarId = null;
  st.prompt = null;
  st.edgeWarn = false;
  player.hangGrab = null;
  player.carVy = 0;
  player.runCharge = 0;
  player.stunT = 0;
  player.downT = 0;
  player.coyoteT = 0;
  player.jumpBufferT = 0;
  player._jumpLatchT = 0;
  player._interactLatchT = 0;
}

/**
 * Sauber zurück: auf Terrain aufsetzen, vy=0, mode ground.
 * Kein Sturzschaden aus 400 m — der klassische Godmode-Bug.
 * @param {object} player
 * @param {object} ctx
 */
export function exitGodMode(player, ctx) {
  const st = player.state;
  st.velocity.set(0, 0, 0);
  st.onCarId = null;
  player.hangGrab = null;
  player.carVy = 0;
  player.stunT = 0;
  player.downT = 0;
  player.coyoteT = 0;
  player.jumpBufferT = 0;

  // Void (Brücke neben Gleis): zur sicheren Seite des Planums ziehen.
  let proj = projectToTrack(st.pos.x, st.pos.z, ctx.trackHint ?? st.trackS);
  if (isVoidAt(proj.s, proj.lateral)) {
    const fr = trackFrame(proj.s, _frame);
    // 3 m seitlich vom Gleis — steht man, ohne in den Abgrund zu fallen.
    const side = Math.sign(proj.lateral) || 1;
    const lat = side * 3.2;
    st.pos.x = fr.pos.x + fr.right.x * lat;
    st.pos.z = fr.pos.z + fr.right.z * lat;
    proj = projectToTrack(st.pos.x, st.pos.z, proj.s);
  }

  const groundY = terrainHeight(st.pos.x, st.pos.z, proj.s);
  // Endliche Position erzwingen — NaN aus entgleistem Flug abfangen.
  if (!Number.isFinite(st.pos.x) || !Number.isFinite(st.pos.z) || !Number.isFinite(groundY)) {
    // Notfall: Spawn-Nähe (Südhof) — besser als NaN-Tod.
    const fr = trackFrame(260, _frame);
    st.pos.set(fr.pos.x + fr.right.x * 8, fr.pos.y, fr.pos.z + fr.right.z * 8);
    st.pos.y = terrainHeight(st.pos.x, st.pos.z, 260);
  } else {
    st.pos.y = groundY;
  }

  st.grounded = true;
  st.mode = 'ground';
  st.crouching = false;
  st.prompt = null;
  st.edgeWarn = false;
  st.canGrab = false;
  st.grabCarId = null;

  // trackS frisch, damit andere Systeme (Terrain-Streaming) nachziehen.
  const p2 = projectToTrack(st.pos.x, st.pos.z, proj.s);
  st.trackS = p2.s;
  st.lateral = p2.lateral;
  if (ctx) ctx.trackHint = p2.s;
}

/**
 * Ein Fixed-Substep Freiflug. Keine Gravitation, keine Kollision.
 * WASD in Blickrichtung, Space hoch, Ducken/Strg runter, Shift = Turbo.
 * @param {object} player
 * @param {number} dt
 * @param {object} ctx
 */
export function stepFly(player, dt, ctx) {
  const st = player.state;
  const input = ctx.input;
  const axis = input && typeof input.moveAxis === 'function'
    ? input.moveAxis()
    : { x: 0, y: 0 };

  const sy = Math.sin(st.yaw);
  const cy = Math.cos(st.yaw);
  const cp = Math.cos(st.pitch);
  const sp = Math.sin(st.pitch);

  // Vor/zurück entlang Blick (inkl. Pitch), Strafe horizontal.
  _wish.x = axis.y * sy * cp + axis.x * cy;
  _wish.y = axis.y * sp;
  _wish.z = -axis.y * cy * cp + axis.x * sy;

  // Hoch/runter unabhängig vom Pitch — sonst ist „geradeaus schauen + steigen" umständlich.
  let vert = 0;
  if (input?.down?.('jump')) vert += 1;
  // crouch-Action oder Control-Tasten (KeyC teilt sich mit Foto — nur Control hier).
  if (input?.down?.('crouch')) vert -= 1;
  else if (typeof input?.key === 'function') {
    if (input.key('ControlLeft') || input.key('ControlRight')) vert -= 1;
  }
  _wish.y += vert;

  const len = Math.hypot(_wish.x, _wish.y, _wish.z);
  if (len > 1e-8) {
    _wish.x /= len;
    _wish.y /= len;
    _wish.z /= len;
  } else {
    _wish.x = 0;
    _wish.y = 0;
    _wish.z = 0;
  }

  const fast = !!(input?.down?.('run'));
  const speed = fast ? FLY_SPEED_FAST : FLY_SPEED;

  st.velocity.x = _wish.x * speed;
  st.velocity.y = _wish.y * speed;
  st.velocity.z = _wish.z * speed;

  st.pos.x += st.velocity.x * dt;
  st.pos.y += st.velocity.y * dt;
  st.pos.z += st.velocity.z * dt;

  st.mode = 'fly';
  st.grounded = false;
  st.crouching = false;
  st.canGrab = false;
  st.grabCarId = null;
  st.prompt = null;
  st.edgeWarn = false;
  st.onCarId = null;
}

export function isGodMode(state) {
  return state?.mode === 'fly';
}
